import React, { useState, useEffect } from 'react';
import { Plus, Search, Calendar, Edit, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { confirmAction } from '../../utils/confirmToast';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import Drawer from '../../components/common/Drawer/Drawer';
import SearchableSelect from '../../components/common/SearchableSelect';
import api from '../../api/axios';
import styles from './Notices.module.css';

const Notices = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
  const userRole = userInfo.role;

  useEffect(() => {
    fetchNotices();
  }, []);

  const fetchNotices = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/community/notices');
      setNotices(data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  const [formData, setFormData] = useState({ title: '', category: 'General', content: '', meetingDateTime: '', meetingLocation: '', targetAudience: 'All' });

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const openDrawerForAdd = () => {
    setEditingId(null);
    setFormData({ title: '', category: 'General', content: '', meetingDateTime: '', meetingLocation: '', targetAudience: 'All' });
    setIsDrawerOpen(true);
  };

  const handleEditClick = (notice) => {
    setEditingId(notice._id);
    setFormData({ 
      title: notice.title, 
      category: notice.category, 
      content: notice.content,
      meetingDateTime: notice.meetingDateTime || '',
      meetingLocation: notice.meetingLocation || '',
      targetAudience: notice.targetAudience || 'All'
    });
    setIsDrawerOpen(true);
  };

  const handleDelete = async (id) => {
    confirmAction("Are you sure you want to delete this notice?", async () => {
      try {
        await api.delete(`/community/notices/${id}`);
        setNotices(notices.filter(n => n._id !== id));
        toast.success('Notice deleted successfully');
      } catch (err) {
        toast.error('Failed to delete notice');
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        date: editingId ? undefined : new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
      };

      if (editingId) {
        const { data } = await api.put(`/community/notices/${editingId}`, submitData);
        setNotices(notices.map(n => n._id === editingId ? data : n));
        toast.success('Notice updated successfully');
      } else {
        const { data } = await api.post('/community/notices', submitData);
        setNotices([data, ...notices]);
        toast.success('Notice created successfully');
      }
      setIsDrawerOpen(false);
      setEditingId(null);
      setFormData({ title: '', category: 'General', content: '', meetingDateTime: '', meetingLocation: '', targetAudience: 'All' });
    } catch (err) {
      toast.error(editingId ? 'Failed to update notice' : 'Failed to create notice');
    }
  };

  return (
    <div className={styles.container}>
      <PageHeader 
        title="Notice Board" 
        breadcrumb={['Community', 'Notices']} 
        actionButton={
          <button className={styles.addBtn} onClick={openDrawerForAdd}>
            <Plus size={16} /> Create Notice
          </button>
        }
      />

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.searchBox}>
            <Search size={16} className={styles.searchIcon} />
            <input type="text" placeholder="Search notices..." />
          </div>
        </div>

        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Notice Title</th>
                <th>Category</th>
                <th>Target</th>
                <th>Date Published</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan="5" style={{textAlign: 'center'}}>Loading notices...</td></tr> : notices.map(n => (
                <tr key={n._id}>
                  <td>
                    <strong>{n.title}</strong>
                    {(n.meetingDateTime || n.meetingLocation) && (
                      <div style={{ display: 'flex', gap: '12px', fontSize: '11px', color: '#64748b', marginTop: '4px', fontWeight: '500' }}>
                        {n.meetingDateTime && <span>📅 {
                          !isNaN(new Date(n.meetingDateTime).getTime())
                            ? new Date(n.meetingDateTime).toLocaleString('en-IN', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true
                              })
                            : n.meetingDateTime
                        }</span>}
                        {n.meetingLocation && <span>📍 {n.meetingLocation}</span>}
                      </div>
                    )}
                  </td>
                  <td><span className={styles.badgeCategory}>{n.category}</span></td>
                  <td>
                    <span className={`${styles.badgeCategory} ${n.targetAudience === 'Resident' ? styles.targetResident : n.targetAudience === 'Security' ? styles.targetSecurity : ''}`} style={{ backgroundColor: n.targetAudience === 'Resident' ? '#eff6ff' : n.targetAudience === 'Security' ? '#faf5ff' : '#f1f5f9', color: n.targetAudience === 'Resident' ? '#3b82f6' : n.targetAudience === 'Security' ? '#a855f7' : '#64748b' }}>
                      {n.targetAudience || 'All'}
                    </span>
                  </td>
                  <td><span className={styles.date}><Calendar size={12} /> {n.date}</span></td>
                  <td>
                    <span className={`${styles.badgeStatus} ${styles.active}`}>
                      Active
                    </span>
                  </td>
                  <td>
                    <div className={styles.actionBtns}>
                      <button className={styles.btnEdit} onClick={() => handleEditClick(n)}><Edit size={14} /></button>
                      <button className={styles.btnDelete} onClick={() => handleDelete(n._id)}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={styles.mobileList}>
          {loading ? <p style={{padding: '16px', textAlign: 'center'}}>Loading notices...</p> : notices.map(n => (
            <div key={n._id} className={styles.mobileCard}>
              <div className={styles.cardTop}>
                <strong>{n.title}</strong>
                <span className={`${styles.badgeStatus} ${styles.active}`}>Active</span>
              </div>
              
              <div className={styles.cardMid}>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <span className={styles.badgeCategory}>{n.category}</span>
                  <span className={styles.badgeCategory} style={{ backgroundColor: n.targetAudience === 'Resident' ? '#eff6ff' : n.targetAudience === 'Security' ? '#faf5ff' : '#f1f5f9', color: n.targetAudience === 'Resident' ? '#3b82f6' : n.targetAudience === 'Security' ? '#a855f7' : '#64748b' }}>
                    {n.targetAudience || 'All'}
                  </span>
                </div>
                <span className={styles.date}><Calendar size={12} /> {n.date}</span>
                
                {(n.meetingDateTime || n.meetingLocation) && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '11px', color: '#64748b', marginTop: '8px', fontWeight: '500' }}>
                    {n.meetingDateTime && <span>📅 {
                      !isNaN(new Date(n.meetingDateTime).getTime())
                        ? new Date(n.meetingDateTime).toLocaleString('en-IN', {
                            day: '2-digit', month: 'short', year: 'numeric',
                            hour: '2-digit', minute: '2-digit', hour12: true
                          })
                        : n.meetingDateTime
                    }</span>}
                    {n.meetingLocation && <span>📍 {n.meetingLocation}</span>}
                  </div>
                )}
              </div>

              <div className={styles.cardBottom}>
                <div className={styles.actionBtns}>
                  <button className={styles.btnEdit} style={{flex: 1, display: 'flex', justifyContent: 'center'}} onClick={() => handleEditClick(n)}><Edit size={16} /></button>
                  <button className={styles.btnDelete} style={{flex: 1, display: 'flex', justifyContent: 'center'}} onClick={() => handleDelete(n._id)}><Trash2 size={16} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Drawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} title={editingId ? "Edit Notice" : "Create Notice"}>
        <form className="drawerForm" onSubmit={handleSubmit}>
          <div className="formGroup">
            <label>Notice Title</label>
            <input name="title" value={formData.title} onChange={handleChange} required placeholder="e.g. Water Cut" />
          </div>
          <div className="formGroup">
            <label>Category</label>
            <SearchableSelect name="category" value={formData.category} onChange={handleChange} id="category-select">
              <option value="General">General</option>
              <option value="Maintenance">Maintenance</option>
              <option value="Security">Security</option>
              <option value="Parking">Parking</option>
              <option value="Electricity">Electricity</option>
              <option value="Water">Water</option>
              <option value="Meeting">Meeting</option>
              <option value="Emergency">Emergency</option>
              <option value="Rules">Rules / Guidelines</option>
            </SearchableSelect>
          </div>
          <div className="formGroup">
            <label>Target Audience</label>
            <select name="targetAudience" value={formData.targetAudience} onChange={handleChange}>
              <option value="All">All (Everyone)</option>
              <option value="Resident">Residents Only</option>
              <option value="Security">Security Only</option>
              {userRole === 'superadmin' && <option value="Admin">Admins Only</option>}
            </select>
          </div>
          <div className="formGroup">
            <label>Date & Time (Optional)</label>
            <input type="datetime-local" name="meetingDateTime" value={formData.meetingDateTime} onChange={handleChange} />
          </div>
          <div className="formGroup">
            <label>Location (Optional)</label>
            <input name="meetingLocation" value={formData.meetingLocation} onChange={handleChange} placeholder="e.g. Clubhouse, Block A" />
          </div>
          <div className="formGroup">
            <label>Notice Content</label>
            <textarea name="content" value={formData.content} onChange={handleChange} required rows="6" placeholder="Write the full notice here..."></textarea>
          </div>
          
          <div className="formActions">
            <button type="button" className="btnCancel" onClick={() => setIsDrawerOpen(false)}>Cancel</button>
            <button type="submit" className="btnSubmit">{editingId ? "Update Notice" : "Publish Notice"}</button>
          </div>
        </form>
      </Drawer>
    </div>
  );
};

export default Notices;
